import { format, parseISO } from 'date-fns';

/**
 * Pure helpers for the patient "Files" tab.
 *
 * The Files tab aggregates every `Attachment` row for a horse and labels each
 * one with a human-readable "source" and (where applicable) the SOAP note it
 * belongs to. All of this is derived client-side from the flat attachment rows
 * the existing `GET /attachments` endpoint already returns — no backend change.
 *
 * Keeping the derivation pure makes it unit-testable in isolation and keeps the
 * component a thin render layer.
 */

/** Subset of the Attachment row shape the Files tab consumes. */
export interface PatientAttachment {
  id: string;
  fileName: string;
  mimeType?: string | null;
  fileSize?: number | null;
  storageUrl?: string | null;
  category?: string | null;
  parentType?: string | null;
  parentId?: string | null;
  createdAt: string;
}

/** Minimal SOAP-note shape used to resolve the "belongs to" label. The
 *  `/soap-notes` payload already carries provider + a full timestamp. */
export interface SoapNoteIndexEntry {
  id: string;
  templateUsed?: string | null;
  date?: string | null;
  provider?: { name?: string | null; professionalTitle?: string | null } | null;
}

export type FileSource = 'soap' | 'lab' | 'photo' | 'document';

export interface PatientFileRow {
  id: string;
  fileName: string;
  mimeType?: string | null;
  fileSize?: number | null;
  storageUrl?: string | null;
  createdAt: string;
  source: FileSource;
  sourceLabel: string;
  /** Human-readable record this file is attached to, or null when standalone. */
  belongsTo: string | null;
  /** Secondary line (e.g. provider), or null. */
  belongsToSub: string | null;
  /** The SOAP note id this file belongs to, when resolvable — lets the Files
   *  tab open the exact note (disambiguating multiple same-day notes). */
  soapNoteId: string | null;
}

/**
 * Categories that originate from lab / imaging / diagnostic flows. `parentType`
 * 'medical_record' is the read-filter the chart already uses for these, so it
 * counts too.
 *
 * These are the ACTUAL category strings written by the imaging/lab upload sites
 * (keystone.controller / keystone-poller.service / discharge-summaries.service
 * write `imaging_thumbnail` + `imaging_report` with NO parentType), plus the
 * schema-comment example `lab_report`. Categories are free-form strings with no
 * enum, so this must enumerate the real persisted values — not a guessed list.
 */
const LAB_CATEGORIES = new Set([
  'imaging_thumbnail',
  'imaging_report',
  'lab_report',
  'lab',
  'lab_result',
]);

export const SOURCE_LABELS: Record<FileSource, string> = {
  soap: 'SOAP note',
  lab: 'Lab & Diagnostics',
  photo: 'Photo',
  document: 'Document',
};

export interface SourceFilterDef {
  key: 'all' | FileSource;
  label: string;
}

/** Filter chips shown above the Files table, in display order. */
export const SOURCE_FILTERS: SourceFilterDef[] = [
  { key: 'all', label: 'All' },
  { key: 'soap', label: 'SOAP notes' },
  { key: 'lab', label: 'Lab & Diagnostics' },
  { key: 'photo', label: 'Photos' },
  { key: 'document', label: 'Documents' },
];

/** Classify a raw attachment into one of the Files-tab source buckets. */
export function classifySource(att: {
  category?: string | null;
  parentType?: string | null;
}): FileSource {
  const category = (att.category || '').toLowerCase();
  const parentType = (att.parentType || '').toLowerCase();
  if (category === 'patient_photo') return 'photo';
  if (parentType === 'soap_note') return 'soap';
  if (parentType === 'medical_record' || LAB_CATEGORIES.has(category)) return 'lab';
  return 'document';
}

function safeDate(iso?: string | null): string | null {
  if (!iso) return null;
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return null;
  }
}

/** Date + time, so two SOAP notes on the same day are distinguishable. */
function safeDateTime(iso?: string | null): string | null {
  if (!iso) return null;
  try {
    return format(parseISO(iso), 'MMM d, yyyy · h:mm a');
  } catch {
    return null;
  }
}

function providerLabel(p?: SoapNoteIndexEntry['provider']): string | null {
  const name = p?.name?.trim();
  if (!name) return null;
  return `Dr. ${name}`;
}

/**
 * Turn flat attachment rows + a SOAP-note index into display rows with a
 * resolved "belongs to" label. SOAP-linked files resolve to
 * `"<template> · <date>"`; lab/photo files get a generic source label; plain
 * uploads have no parent (null).
 */
export function buildFileRows(
  attachments: PatientAttachment[],
  soapNotes: SoapNoteIndexEntry[],
): PatientFileRow[] {
  const soapIndex = new Map<string, SoapNoteIndexEntry>();
  soapNotes.forEach((n) => {
    if (n && n.id) soapIndex.set(n.id, n);
  });

  return attachments.map((att) => {
    const source = classifySource(att);
    let belongsTo: string | null = null;
    let belongsToSub: string | null = null;
    let soapNoteId: string | null = null;

    if (source === 'soap' && att.parentId) {
      const note = soapIndex.get(att.parentId);
      const tmpl = note?.templateUsed?.trim() || 'SOAP Note';
      // Date + time disambiguates multiple notes on the same day.
      const noteDateTime = safeDateTime(note?.date) || safeDateTime(att.createdAt);
      belongsTo = noteDateTime ? `${tmpl} · ${noteDateTime}` : tmpl;
      belongsToSub = providerLabel(note?.provider);
      // Only clickable when we can resolve the full note from the index.
      soapNoteId = note ? att.parentId : null;
    } else if (source === 'lab') {
      belongsTo = 'Lab & Diagnostics';
    } else if (source === 'photo') {
      belongsTo = 'Patient photo';
    }

    return {
      id: att.id,
      fileName: att.fileName,
      mimeType: att.mimeType,
      fileSize: att.fileSize,
      storageUrl: att.storageUrl,
      createdAt: att.createdAt,
      source,
      sourceLabel: SOURCE_LABELS[source],
      belongsTo,
      belongsToSub,
      soapNoteId,
    };
  });
}

/** Per-source counts for the filter-chip badges (plus an `all` total). */
export function countBySource(
  rows: PatientFileRow[],
): Record<'all' | FileSource, number> {
  const counts: Record<'all' | FileSource, number> = {
    all: rows.length,
    soap: 0,
    lab: 0,
    photo: 0,
    document: 0,
  };
  rows.forEach((r) => {
    counts[r.source] += 1;
  });
  return counts;
}

export function formatFileSize(bytes?: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatFileDate(iso?: string | null): string {
  return safeDate(iso) || '—';
}

/**
 * `GET /attachments` and `GET /soap-notes` return EITHER a bare array OR a
 * `{ data: [...] }` paginated envelope (depending on `?page`). Normalize both
 * to an array. Defensive so a future server pagination change can't silently
 * break a consumer.
 */
export function normalizeList<T>(data: any): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && Array.isArray(data.data)) return data.data as T[];
  return [];
}

/**
 * Build a `soapNoteId -> attached-file count` map from a horse's attachments,
 * for the paperclip chip on collapsed SOAP cards/timeline rows. Shared by both
 * chart layouts so the soap_note discriminator + counting live in one place.
 */
export function countSoapAttachmentsByNote(
  attachments: Array<{ parentType?: string | null; parentId?: string | null }>,
): Record<string, number> {
  const counts: Record<string, number> = {};
  attachments.forEach((a) => {
    if (a?.parentType === 'soap_note' && a?.parentId) {
      counts[a.parentId] = (counts[a.parentId] || 0) + 1;
    }
  });
  return counts;
}
